import * as React from 'react';
import { useQuery } from '@apollo/client';
import MemberInfo from './member-info';
import MemberParticipationOverview from './member-participation-overview';
import MemberHistory from './member-history';
import { DateService, DateFormat } from 'frontend-toolkit';
import { APP_URL } from 'core/constants';

import * as ShowMember from '../../core/gql/queries/show-member';
import * as ShowMemberHistory from '../../core/gql/queries/show-member-history';
import { Tabs, Avatar } from '@teamsnap/snap-ui';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'core/routes';
import { useParams } from 'react-router';
import MemberInvoicesContainer from './member-financials/member-invoices/member-invoices-container';
import MemberRegistrationsContainer from './member-financials/member-registrations/member-registrations-container';
import { AuthContext, isOneOf, Role } from 'auth';

export interface Props {
  orgId: number;
  memberId: number;
}

enum FinancialsAdminMemberTabs {
  PARTICIPATION = 'participation',
  INVOICES = 'invoices',
  REGISTRATIONS = 'registrations',
  HISTORY = 'history',
}

enum MemberTabs {
  PARTICIPATION = 'participation',
  HISTORY = 'history',
}

const getDefaultMemberTab = (
  path: string,
  canViewFinancials: boolean
): FinancialsAdminMemberTabs | MemberTabs => {
  const tabs = canViewFinancials ? FinancialsAdminMemberTabs : MemberTabs;
  const foundTab = (Object.values(tabs) as string[]).find(
    (tab) => tab === path
  );
  return (
    (foundTab as FinancialsAdminMemberTabs | MemberTabs) || tabs.PARTICIPATION
  );
};

const MemberView: React.FC<Props> = ({ orgId, memberId }) => {

  const query = useQuery(ShowMember.QUERY, {
    variables: { organizationId: orgId, memberId: memberId },
    fetchPolicy: 'cache-and-network',
  });

  const member = query.data?.member;
  const date = member?.joinedAt ? new Date(member.joinedAt) : '';
  const joinedAtDateString = date
    ? DateService.dateToString(date, DateFormat.SHORT_DATE)
    : '';
  const seasonParticipants = member?.seasonParticipants;
  const historyQuery = useQuery(ShowMemberHistory.QUERY, {
    variables: { organizationId: orgId, memberId: memberId },
    fetchPolicy: 'cache-and-network',
  });

  const memberHistoryActivities = historyQuery.data?.activityLogs;

  const userContext = React.useContext(AuthContext);

  const hasPermissionsForMemberFinancials =
    userContext?.user !== null
      ? isOneOf(
          `${orgId}`,
          userContext.user,
          Role.Admin,
          Role.Owner,
          Role.OrgAdmin,
          Role.ProgramAdmin
        )
      : false;

  const location = useLocation();
  const history = useHistory();

  const params = useParams<{
    orgId: string;
    memberId: string;
    memberTab: string;
  }>();

  const [memberTab, setMemberTab] = React.useState<
    MemberTabs | FinancialsAdminMemberTabs
  >(
    getDefaultMemberTab(
      location.pathname.split('/').at(-1),
      hasPermissionsForMemberFinancials
    )
  );

  React.useEffect(() => {
    if (params.orgId && params.memberId && !params.memberTab) {
      history.replace(
        generatePath<string>(ROUTES.MEMBER, {
          orgId,
          memberId,
          memberTab,
        })
      );
    }
  }, [params]);

  const tabs = [
    {
      label: 'Participation',
      value: 'participation',
      content: (
        <div className="sui-flex sui-gap-x-2 sui-mt-2">
          <div className="sui-w-4/12">
            <MemberInfo member={member} />
          </div>
          <div className="sui-w-8/12">
            <MemberParticipationOverview
              orgId={orgId}
              memberId={memberId}
              seasonParticipants={seasonParticipants}
            />
          </div>
        </div>
      ),
    },
  ];

  if (hasPermissionsForMemberFinancials) {
    tabs.push({
      label: 'Invoices',
      value: 'invoices',
      content: (
        <div className="sui-mt-2">
          <MemberInvoicesContainer orgId={orgId} memberId={memberId} />
        </div>
      ),
    });
  }

  if (hasPermissionsForMemberFinancials) {
    tabs.push({
      label: 'Registrations',
      value: 'registrations',
      content: (
        <div className="sui-mt-2">
          <MemberRegistrationsContainer orgId={orgId} memberId={memberId} />
        </div>
      ),
    });
  }

  tabs.push({
    label: 'History',
    value: 'history',
    content: (
      <div className="sui-flex sui-gap-x-2 sui-mt-2">
        <div className="sui-w-8/12">
          <MemberHistory
            orgId={orgId}
            memberId={memberId}
            memberHistoryActivities={memberHistoryActivities}
          />
        </div>
      </div>
    ),
  });

  return (
    <>
      <div className="page-content-header Grid u-flexAlignItemsCenter">
        <div
          className="Grid-cell u-xs-size1of24"
          style={{ marginRight: '50px' }}
        >
          <Avatar
            type="placeholder"
            src={`${APP_URL}/images/avatar-placeholder.svg`}
            size="large"
          />
        </div>
        <div className="Grid-cell u-xs-size1of2">
          <h2>
            {member?.firstName || ''} {member?.lastName || ''}
          </h2>
          {joinedAtDateString ? (
            <h4>
              Joined <time>{joinedAtDateString}</time>
            </h4>
          ) : (
            ''
          )}
        </div>
      </div>
      <Tabs
        value={memberTab}
        onChange={(nextTab) => {
          setMemberTab(nextTab as MemberTabs | FinancialsAdminMemberTabs);
          history.replace(
            generatePath<string>(ROUTES.MEMBER, {
              orgId,
              memberId,
              memberTab: nextTab,
            })
          );
        }}
        tabs={tabs}
      />
    </>
  );
};

export default MemberView;
